import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import theme from './theme/theme';
import AppShell from './components/AppShell';
import ProtectedRoute from './components/ProtectedRoute';
import RequirePermission from './components/RequirePermission';
import { ErrorBoundary } from './components/ui';
import { AuthProvider } from './context/AuthContext';
import { MeProvider } from './context/MeContext';
import { SnackbarProvider } from './components/Snackbar';

import AuthTabs from './components/Auth/AuthTabs';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import OtpVerify from './components/Auth/OtpVerify';
import Signup from './pages/Signup';
import Join from './pages/Join';
import DeleteUser from './pages/DeleteUser';

import Dashboard from './pages/Dashboard';
import Patient from './pages/Patient';
import PatientDetail from './pages/PatientDetail';
import PatientHistory from './pages/PatientHistory';
import Domains from './pages/Domain';
import DomainForm from './components/DomainForm';
import ReportDetail from './pages/ReportDetail';
import NotFound from './pages/NotFound';

import Roles from './pages/admin/Roles';
import Users from './pages/admin/Users';
import Billing from './pages/admin/Billing';
import ClinicSettings from './pages/admin/ClinicSettings';

import Tenants from './pages/platform/Tenants';
import TenantDetail from './pages/platform/TenantDetail';
import PlatformOverview from './pages/platform/Overview';
import PlatformUsers from './pages/platform/Users';
import PlatformAuditLog from './pages/platform/AuditLog';

/** Signed in + approved into a clinic. */
const Guarded = ({ children }) => <ProtectedRoute>{children}</ProtectedRoute>;

/** Guarded, and holding at least one of `anyOf` (or platform admin). */
const Restricted = ({ anyOf, platform, children }) => (
  <ProtectedRoute>
    <RequirePermission anyOf={anyOf} platform={platform}>{children}</RequirePermission>
  </ProtectedRoute>
);

function AppRoutes() {
  return (
    <AppShell>
      <Routes>
        {/* Public / onboarding */}
        <Route path="/" element={<AuthTabs />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/join" element={<Join />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/otp-verify" element={<OtpVerify />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/delete-user" element={<DeleteUser />} />

        {/* Clinical */}
        <Route path="/dashboard" element={<Guarded><Dashboard /></Guarded>} />
        <Route path="/patient" element={<Guarded><Patient /></Guarded>} />
        <Route path="/patient/:patientId" element={<Guarded><PatientDetail /></Guarded>} />
        <Route path="/patient/:patientId/history" element={<Guarded><PatientHistory /></Guarded>} />
        <Route path="/patient/:patientId/report" element={<Guarded><ReportDetail /></Guarded>} />

        {/*
          Assessment lives under the patient URL. It previously read the patient
          from router state, so a refresh silently dropped it and scoring
          continued against nobody.
        */}
        <Route path="/patient/:patientId/assess" element={<Guarded><Domains /></Guarded>} />
        <Route path="/patient/:patientId/assess/:code" element={<Guarded><DomainForm /></Guarded>} />

        {/* Legacy paths kept as redirects so existing links and bookmarks survive. */}
        <Route path="/domains" element={<Navigate to="/patient" replace />} />
        <Route path="/domains/:code" element={<Navigate to="/patient" replace />} />
        <Route path="/report" element={<Navigate to="/patient" replace />} />

        {/* Clinic administration */}
        <Route path="/users" element={<Restricted anyOf={['user:view']}><Users /></Restricted>} />
        <Route path="/roles" element={<Restricted anyOf={['role:view']}><Roles /></Restricted>} />
        <Route path="/billing" element={<Restricted anyOf={['billing:view']}><Billing /></Restricted>} />
        <Route path="/clinic" element={<Restricted anyOf={['tenant:settings']}><ClinicSettings /></Restricted>} />

        {/* Platform super-admin */}
        <Route path="/platform" element={<Restricted platform><PlatformOverview /></Restricted>} />
        <Route path="/platform/tenants" element={<Restricted platform><Tenants /></Restricted>} />
        <Route path="/platform/tenants/:tenantId" element={<Restricted platform><TenantDetail /></Restricted>} />
        <Route path="/platform/users" element={<Restricted platform><PlatformUsers /></Restricted>} />
        <Route path="/platform/audit" element={<Restricted platform><PlatformAuditLog /></Restricted>} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthProvider>
          <MeProvider>
            <SnackbarProvider>
              <ErrorBoundary>
                <Router>
                  <AppRoutes />
                </Router>
              </ErrorBoundary>
            </SnackbarProvider>
          </MeProvider>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
